import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { sendContactMessage, trackVisitor, contactActions } from "@/app/store";
import { PROFILE } from "@/data/portfolio";
import { CONTACT } from "@/constants/testIds";
import SectionHeading from "@/components/molecules/SectionHeading";
import MagneticButton from "@/components/molecules/MagneticButton";
import { FiGithub, FiLinkedin, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import { toast } from "sonner";

export default function Contact() {
  const dispatch = useDispatch();
  const { submitting, success, error } = useSelector((s) => s.contact);
  const visitor = useSelector((s) => s.visitor);

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [touched, setTouched] = useState({});

  useEffect(() => {
    dispatch(trackVisitor());
  }, [dispatch]);

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const onBlur = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  const errors = {
    name: form.name.trim() ? "" : "Name is required",
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? "" : "Valid email required",
    message: form.message.trim().length >= 10 ? "" : "Min 10 characters",
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    if (hasErrors) return;
    const ok = await dispatch(sendContactMessage(form));
    if (ok) {
      toast.success("Message sent — I'll get back within 24h.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTouched({});
      setTimeout(() => dispatch(contactActions.resetContact()), 4000);
    } else {
      toast.error("Could not send — try again or email me directly.");
    }
  };

  return (
    <section id="contact" data-testid={CONTACT.section} className="section relative">
      <div className="container-x">
        <SectionHeading
          overline="06 — Contact"
          title="Have a problem worth solving?"
          description="I&apos;m currently open to senior frontend roles, contract gigs and serious side-projects."
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-5 space-y-8">
            <h3 className="font-display text-3xl md:text-5xl leading-[1.05]">
              Let&apos;s build something
              <br />
              <span className="text-[color:var(--accent)]">people actually use.</span>
            </h3>

            <div className="space-y-3 font-mono text-sm text-[color:var(--text-muted)]">
              <a
                href={`mailto:${PROFILE.email}`}
                className="flex items-center gap-3 hover:text-[color:var(--accent)] transition"
              >
                <FiMail /> {PROFILE.email}
              </a>
              <a
                href="tel:+918982462563"
                className="flex items-center gap-3 hover:text-[color:var(--accent)] transition"
              >
                <FiPhone /> {PROFILE.phone}
              </a>
              <div className="flex items-center gap-3">
                <FiMapPin /> {PROFILE.location}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={PROFILE.github}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                className="w-11 h-11 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
              >
                <FiGithub />
              </a>
              <a
                href={PROFILE.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-11 h-11 rounded-full border border-[color:var(--border)] flex items-center justify-center hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
              >
                <FiLinkedin />
              </a>
            </div>

            <div
              data-testid={CONTACT.visitorCount}
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] font-mono text-xs"
            >
              <span className="w-2 h-2 rounded-full bg-[color:var(--accent)] pulse-dot" />
              <span className="text-[color:var(--text-muted)]">visitors</span>
              <span className="text-[color:var(--text)]">{visitor.count.toLocaleString()}</span>
            </div>
          </div>

          {/* Right form */}
          <form
            name="contact"
            method="POST"
            action="/"
            onSubmit={onSubmit}
            data-testid={CONTACT.form}
            className="lg:col-span-7 space-y-6"
            noValidate
            data-netlify="true"
            netlify-honeypot="bot-field"
          >
            <input type="hidden" name="form-name" value="contact" />
            <p className="hidden">
              <label>
                Do not fill this out: <input name="bot-field" onChange={setField("bot-field")} />
              </label>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Name"
                error={touched.name && errors.name}
                input={
                  <input
                    data-testid={CONTACT.name}
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={setField("name")}
                    onBlur={onBlur("name")}
                    placeholder="Your name"
                    autoComplete="name"
                    className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-[color:var(--accent)] outline-none py-3 font-body"
                  />
                }
              />
              <Field
                label="Email"
                error={touched.email && errors.email}
                input={
                  <input
                    data-testid={CONTACT.email}
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    onBlur={onBlur("email")}
                    placeholder="you@company.com"
                    autoComplete="email"
                    className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-[color:var(--accent)] outline-none py-3 font-body"
                  />
                }
              />
            </div>

            <Field
              label="Subject"
              input={
                <input
                  data-testid={CONTACT.subject}
                  name="subject"
                  type="text"
                  value={form.subject}
                  onChange={setField("subject")}
                  placeholder="What's this about?"
                  className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-[color:var(--accent)] outline-none py-3 font-body"
                />
              }
            />

            <Field
              label="Message"
              error={touched.message && errors.message}
              input={
                <textarea
                  data-testid={CONTACT.message}
                  name="message"
                  value={form.message}
                  onChange={setField("message")}
                  onBlur={onBlur("message")}
                  rows={5}
                  placeholder="Tell me about the project, role or idea..."
                  className="w-full bg-transparent border-b border-[color:var(--border)] focus:border-[color:var(--accent)] outline-none py-3 font-body resize-none"
                />
              }
            />

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <MagneticButton
                as="button"
                type="submit"
                disabled={submitting}
                data-testid={CONTACT.submit}
                className="bg-[color:var(--accent)] text-white hover:opacity-90 disabled:opacity-60 px-7 py-3.5"
                strength={12}
              >
                {submitting ? "Sending..." : "Send message →"}
              </MagneticButton>
              {success && (
                <span data-testid={CONTACT.success} className="font-mono text-sm text-emerald-400">
                  ✓ Sent — talk soon.
                </span>
              )}
              {error && (
                <span data-testid={CONTACT.error} className="font-mono text-sm text-red-400">
                  ✕ {error}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, input, error }) {
  return (
    <label className="block">
      <span className="label-mono">{label}</span>
      <div className="mt-1">{input}</div>
      {error && <span className="mt-1 inline-block text-xs font-mono text-red-400">{error}</span>}
    </label>
  );
}
